import React from "react"
import { observer } from "mobx-react-lite"

import Label from "@components/ui/Label/Label"
import List from "@components/ui/List/List"
import Select from "@components/ui/MultiSelect/Select"
import Text from "@components/ui/Typography/Text"
import Icon from "@components/ui/Icon/Icon"
import { useStore } from "@store"
import {
  BrandExpertResolution,
  EXPERT_QUESTION_TYPE_OPTIONS,
  ExpertQuestionType,
} from "@framework/types/brand-expert"

import styles from "./AssignExpertToQuestionModal.module.sass"

export interface RoutingFieldsProps {
  companyId: string | null
  questionType: ExpertQuestionType | null
  onCompanyChange: (companyId: string | null) => void
  onQuestionTypeChange: (questionType: ExpertQuestionType | null) => void
  onResolutionChange: (resolution: BrandExpertResolution | null) => void
}

/**
 * LAB-47: brand and category pickers for "Ask an Expert".
 *
 * The brand list only contains brands that actually have an expert configured,
 * so an empty or unlisted brand is a normal outcome rather than an error - the
 * modal then falls back to the manual expert picker.
 */
export const RoutingFields: React.FC<RoutingFieldsProps> = observer(
  ({
    companyId,
    questionType,
    onCompanyChange,
    onQuestionTypeChange,
    onResolutionChange,
  }) => {
    const {
      brandExpertStore: {
        brandOptions,
        isBrandsLoading,
        loadBrands,
        resolveExpert,
      },
    } = useStore()

    const [resolution, setResolution] =
      React.useState<BrandExpertResolution | null>(null)

    React.useEffect(() => {
      loadBrands()
    }, [])

    React.useEffect(() => {
      let cancelled = false

      if (!companyId) {
        setResolution(null)
        onResolutionChange(null)
        return undefined
      }

      resolveExpert(companyId).then((result) => {
        if (cancelled) return
        setResolution(result)
        onResolutionChange(result)
      })

      return () => {
        cancelled = true
      }
    }, [companyId])

    return (
      <List gutter="16">
        <Label id="expert_brand" label="Brand">
          <Select
            clearable
            searchable
            placeholder="Select a brand (optional)"
            loading={isBrandsLoading}
            options={brandOptions}
            value={companyId}
            onChange={(value) => onCompanyChange(value)}
          />
        </Label>

        <Label id="expert_question_type" label="Question type">
          <Select
            clearable
            placeholder="Select a question type"
            options={EXPERT_QUESTION_TYPE_OPTIONS}
            value={questionType}
            onChange={(value) =>
              onQuestionTypeChange(value as ExpertQuestionType | null)
            }
          />
        </Label>

        {!!companyId && !!resolution?.expert && (
          <Text variant="caption2" className={styles.resolvedExpert}>
            <Icon name="user" />
            {`Assigned expert: ${resolution.expert.name}`}
          </Text>
        )}

        {!!companyId && !resolution?.expert && (
          <Text variant="caption2" className={styles.resolvedExpert}>
            <Icon name="info" />
            No expert is configured for this brand yet — choose one below.
          </Text>
        )}
      </List>
    )
  }
)

export default RoutingFields
